// src/components/modals/DetailsModal/ModalHeader.js

import React from 'react';
import { TagIcon, CheckBadgeIcon, AcademicCapIcon, ClockIcon, SparklesIcon, EyeIcon, EyeSlashIcon, PuzzlePieceIcon } from '@heroicons/react/24/outline';
import { srsMapping } from '../../../utils/mappings';
import { useFurigana } from '../../../hooks/useFurigana';
import { useAudioPlayer } from '../../../context/AudioPlayerContext';
import Tippy from '@tippyjs/react';
import WordDisplay from '../../utils/WordDisplay';
import SentenceDisplay from '../../utils/SentenceDisplay';
import PlayButton from '../../utils/PlayButton';
import GlassyBadge from '../../common/GlassyBadge';


const ModalHeader = ({ item, onItemClick, isTopmost, learnedWordIds, onVoiceChange, currentVoice }) => {
  const [showFurigana, setFuriganaVisibility] = useFurigana();
  const { isPlaying, currentTime } = useAudioPlayer();
  const isWord = !!item.word;
  const effectiveSrsStage = item.skipped ? 7 : item.srs_stage;

  return (
    <div className="p-6 md:p-8 bg-gradient-to-br from-background-secondary to-background-primary dark:from-background-primary dark:to-background-secondary border-b-2 border-border">
      <div className="relative">
        <div className="items-start absolute top-0 left-0 flex flex-col md:flex-row md:flex-wrap gap-2">
          <GlassyBadge 
            icon={TagIcon}
            level={item.level}
            size="md"
          >
            N{item.level}
          </GlassyBadge>
          {item.isLearned && (
            <GlassyBadge
              icon={CheckBadgeIcon}
              srsStage={effectiveSrsStage}
              size="md"
            >
              {srsMapping[effectiveSrsStage]}
            </GlassyBadge>
          )}
          {item.tags?.includes('grammar') && (
            <GlassyBadge 
              icon={AcademicCapIcon}
              color="purple"
              size="md"
            >
              Grammar
            </GlassyBadge>
          )}
          {item.tags?.includes('common') && (
            <GlassyBadge
              icon={SparklesIcon}
              color="amber"
              size="md"
            >
              Common
            </GlassyBadge>
          )}
          {!isWord && item.kanji_complexity != null && (
            <GlassyBadge icon={PuzzlePieceIcon} color="blue" size="md">
              {item.kanji_complexity === 61 ? '漢61+' : `漢${item.kanji_complexity}`}
            </GlassyBadge>
          )}
        </div>
        <div className="absolute top-0 right-0">
          {item.isLearned && item.next_review && !item.skipped && (
            <div 
              className="
                relative flex items-center px-3 py-1.5 rounded-full text-xs font-semibold
                backdrop-blur-sm border border-border overflow-hidden
              "
              style={{
                background: 'var(--color-background-secondary)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              {/* Subtle glassmorphic overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />
              
              {/* Content */}
              <div className="relative z-10 flex items-center text-content-secondary">
                <ClockIcon className="h-4 w-4" />
                <span className="ml-1.5">
                  <span className="hidden lg:inline">Next: </span>
                  {new Date(item.next_review.seconds * 1000).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                  })}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center justify-center">
          <Tippy content={showFurigana ? 'Hide Furigana' : 'Show Furigana'} placement="top">
            <button
              onClick={() => setFuriganaVisibility((prev) => !prev)}
              className="p-2 text-content-secondary rounded-full hover:bg-border transition-colors"
            >
              {showFurigana ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
            </button>
          </Tippy>
          <div className="pt-4">
            <PlayButton size={80} isHotkeysEnabled={isTopmost} itemData={item} onVoiceChange={onVoiceChange} currentVoice={currentVoice} />
          </div>
          <div className="mt-10 text-center">
            {isWord ? (
              <WordDisplay item={item} learnedWordIds={learnedWordIds} onItemClick={onItemClick} />
            ) : (
              <SentenceDisplay 
                item={item} 
                onItemClick={onItemClick} 
                learnedWordIds={learnedWordIds}
                currentTime={currentTime}
                isKaraokeMode={isPlaying}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalHeader;